import { useEffect, useState, useRef, useCallback } from "react";

// =============================================================================
// POSITION TYPE
// =============================================================================

interface MousePosition {
  x: number;
  y: number;
}

// =============================================================================
// useTrackMousePosition HOOK
// =============================================================================
// Optimized for 60fps performance using requestAnimationFrame throttling.
// Only triggers React re-renders when the position actually changes.

export const useTrackMousePosition = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
}) => {
  // ---------------------------------------------------------------------------
  // State & Refs
  // ---------------------------------------------------------------------------
  const [mousePosition, setMousePosition] = useState<MousePosition | null>(null);

  // Track pending position to batch updates with RAF
  const pendingPositionRef = useRef<MousePosition | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const isHoveringRef = useRef(false);

  // ---------------------------------------------------------------------------
  // RAF-throttled state update
  // ---------------------------------------------------------------------------
  // Schedules a state update on the next animation frame.
  // This ensures we update at most once per frame (~60fps).
  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current !== null) return; // Already scheduled

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;

      const pending = pendingPositionRef.current;

      // Update state only if position changed (avoid redundant re-renders)
      setMousePosition((current) => {
        if (pending === null && current === null) return current;
        if (pending === null) return null;
        if (current === null) return pending;
        if (current.x === pending.x && current.y === pending.y) return current;
        return pending;
      });
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Store parent elements for cleanup (avoid re-traversing DOM)
    const parentElements: HTMLElement[] = [];

    const handleMouseMove = (e: MouseEvent) => {
      pendingPositionRef.current = { x: e.clientX, y: e.clientY };
      isHoveringRef.current = true;
      scheduleUpdate();
    };

    const handleMouseLeave = () => {
      pendingPositionRef.current = null;
      isHoveringRef.current = false;
      scheduleUpdate();
    };

    const handleScroll = () => {
      // Clear tooltip when scrolling
      pendingPositionRef.current = null;
      isHoveringRef.current = false;
      scheduleUpdate();
    };

    // Add event listeners (passive for better scroll performance)
    element.addEventListener("mousemove", handleMouseMove);
    element.addEventListener("mouseleave", handleMouseLeave);
    element.addEventListener("scroll", handleScroll, { passive: true });

    // Collect and attach scroll listeners to parent containers
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      parentElements.push(parent);
      parent.addEventListener("scroll", handleScroll, { passive: true });
      parent = parent.parentElement;
    }

    // Cleanup
    return () => {
      // Cancel any pending RAF
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      element.removeEventListener("mousemove", handleMouseMove);
      element.removeEventListener("mouseleave", handleMouseLeave);
      element.removeEventListener("scroll", handleScroll);

      // Remove scroll listeners from cached parent elements
      for (const parentEl of parentElements) {
        parentEl.removeEventListener("scroll", handleScroll);
      }
    };
  }, [containerRef, scheduleUpdate]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    mousePosition,
    isHovering: mousePosition !== null,
  };
};
